/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { Button, INodeProps, Module, Node, Popover } from '@msdyn365-commerce-modules/utilities';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import { getUrlSync, RichTextComponent } from '@msdyn365-commerce/core';
import * as React from 'react';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { issueLoyalty, IssueLoyaltyInput } from '@msdyn365-commerce-modules/retail-actions';
import { ToastContainer } from 'react-toastify';
import { IHeaderData } from './header.data';
import { CommerceProperty, Customer, StoreOperationsDataActions, CustomersDataActions } from '@msdyn365-commerce/retail-proxy';
import jwtDecode from 'jwt-decode';
import { subscribeCustomer } from '../../../shared/functions/newsletter';

const headerView: React.FC<IHeaderViewProps & IHeaderProps<IHeaderData>> = (props: IHeaderViewProps & IHeaderProps<IHeaderData>) => {
    const {
        context: {
            actionContext,
            request: { apiSettings, user }
        },
        telemetry: { error, debug, information },
        resources: { logOffButtonText },
        data: { customerInformation },
        preferredStore,
        wishListIconDesktop,
        cartIcon,
        logo,
        search,
        siteOptions,
        signInLink,
        wishListIconMobile,
        menuBar,
        config
    } = props;
    const myProfileLink = getUrlSync('user-account', actionContext);
    const [popOver, togglePopOver] = React.useState<boolean>(false);
    const popOverRef: React.RefObject<HTMLButtonElement> = React.createRef();

    const [scroll, setScroll] = React.useState(false);
    const [modal, setModal] = React.useState<boolean>(false);
    const [height, setHeight] = React.useState(0);
    const headerRef = React.useRef<HTMLDivElement>(null);

    const _issueLoyalty = async () => {
        if (user?.isAuthenticated) {
            const input = new IssueLoyaltyInput(apiSettings);

            issueLoyalty(input, actionContext)
                .then(() => {
                    information('Loyalty Card Issued');
                })
                .catch((err: Error) => {
                    error(err.message);
                    debug('Unable to issue loyalty card');
                });
        }
    };
    React.useEffect(() => {
        headerRef.current && setHeight(headerRef.current.offsetHeight);
        setTimeout(() => {
            // second check in case rendering hasn#t been in order expected
            headerRef.current && setHeight(headerRef.current.offsetHeight);
        }, 100);
    });

    React.useEffect(() => {
        if (height > 0) {
            const searchContainer = document.getElementsByClassName('ms-search-result__sticky-header') as HTMLCollectionOf<HTMLElement>;
            const outerHeader = document.querySelector('body header:first-of-type') as HTMLElement;
            if (searchContainer.length > 0 && outerHeader) {
                // set top of sticky filters so it's below sticky header, with some passing to prevent a gap briefly showing
                const searchHeader = searchContainer[0];
                searchHeader.style.paddingTop = `16px`;
                searchHeader.style.top = `${outerHeader.offsetHeight - 16}px`;
            }
        }
    }, [height]);

    React.useEffect(() => {
        typeof window !== undefined && window.sessionStorage.setItem('mailSent', 'false');
        window.addEventListener('scroll', () => {
            setScroll(window.scrollY > 0);
        });

        async function fetchLoyalty() {
            const {
                data: { loyaltyCard, customerInformation }
            } = props;

            const hasLoyaltyAccount = loyaltyCard.result && loyaltyCard.result.CardTenderTypeValue !== undefined;
            const customerPrimaryLoyalty = customerInformation.result?.ExtensionProperties?.find(
                (item: CommerceProperty) => item.Key === 'BS_PRIMARYLOYALTYCARDID' && !item.Value?.StringValue
            );

            !hasLoyaltyAccount && (await _issueLoyalty());

            if (hasLoyaltyAccount || !customerPrimaryLoyalty) {
                const card = await StoreOperationsDataActions.getCustomerLoyaltyCardsAsync(
                    { callerContext: actionContext, queryResultSettings: {} },
                    null
                );
                const customer: Customer = { ...customerInformation.result! };
                let primaryLoyalty: CommerceProperty[] = [
                    {
                        Key: 'BS_PRIMARYLOYALTYCARDID',
                        Value: { StringValue: card[0].CardNumber || (loyaltyCard && loyaltyCard.result?.CardNumber) }
                    }
                ];
                customer.ExtensionProperties = [...customer.ExtensionProperties!, ...primaryLoyalty];
                const icontext = { callerContext: actionContext };
                await CustomersDataActions.updateAsync(icontext, customer);
            }
        }

        if (user?.isAuthenticated) {
            // Added delay to get he updated result.
            setTimeout(() => {
                fetchLoyalty();
            }, 3000);
        }

        const element = document.getElementsByClassName('search-modal')[0];

        element && element.parentElement && element.parentElement.parentElement!.setAttribute('class', 'removeshadow');
    }, [modal]);

    React.useEffect(() => {
        const hadSubscribeCheck = props.context.request.sessionStorage.getDataFromStorage('hadSubscribeCheck') as { yes: boolean };
        
        if (!hadSubscribeCheck.yes && user.isAuthenticated && customerInformation.result && customerInformation?.result?.ExtensionProperties?.find(item => item.Key ===  'BS_ALLOWRECEIVENEWSLETTER')?.Value?.IntegerValue === 0) {
            const token: any = jwtDecode(user.token);
            if (
                config.timeoutForNewsletterCheck &&
                config.newsletterChoiceTimeClaim &&
                token[config.newsletterChoiceClaim.name] &&
                token[config.newsletterChoiceClaim.name] === config.newsletterChoiceClaim.yesValue &&
                token[config.newsletterChoiceTimeClaim]
            ) {
                // find a tme not too long ago based on the timeout config
                const notLongAgo = new Date(Date.now() - 1000 * config.timeoutForNewsletterCheck);
                const subscribedTime = new Date(token[config.newsletterChoiceTimeClaim] * 1000);
                //jwt date times are seconds so x 1000 because javascript is millieconds
                props.context.request.sessionStorage.setDataInStorage('hadSubscribeCheck', { yes: true });
                if (subscribedTime >= notLongAgo) {
                    subscribeCustomer(customerInformation.result, config.newsletterAttributeName, props.context)
                }
            }
        }
    }, [user, customerInformation]);

    const { HeaderTag, HeaderContainer, HeaderTopBarContainer } = props;

    const toggle = () => setModal(!modal);
    const [modal2, setModal2] = React.useState<boolean>(false);
    const toggle2 = () => setModal2(!modal2);
    return (
        <>
            <ToastContainer position='bottom-left' />

            <Modal className='search-modal' isOpen={modal} toggle={toggle}>
                <ModalBody style={{ padding: 0 }}>
                    <div className='search-modal-body'>
                        <button id='cross-modal' onClick={toggle}>
                            X
                        </button>
                        {_renderReactFragment(search)}
                        {/* {preferredStore}
                        {slots.onlyStoreSelector} */}
                    </div>
                </ModalBody>
            </Modal>

            <div className={scroll ? 'fixed-header' : 'normal-header'} ref={headerRef}>
                <Module {...HeaderTag}>
                    <Node {...HeaderContainer}>
                        <Node {...HeaderTopBarContainer}>
                            <div className='navigation-head'>
                                <div className='search-bar'>
                                    <button className='mobileNavBtn' onClick={toggle2}></button>
                                    {preferredStore}
                                    {_renderReactFragment(siteOptions)}
                                </div>
                                {//placeholder for when scrolling to keep the sticky header the same size
                                scroll ? <div className='placeholder'></div> : null}
                                {logo}
                                <div className='icon-wrapper'>
                                    <div className='header-options'>
                                        <button className='mobileSearchBtn' onClick={toggle}></button>
                                        <div className='header-options search-box'>{_renderReactFragment(search)}</div>
                                        {wishListIconDesktop}
                                        {_renderDesktopAccountBlock(props, popOver, togglePopOver, popOverRef, myProfileLink)}
                                        {cartIcon}
                                    </div>
                                </div>
                            </div>
                        </Node>

                        <Modal className='mobile-nav-modal' isOpen={modal2} toggle={toggle2}>
                            <button id='cross-modal' onClick={toggle2}>
                                X
                            </button>
                            <ModalBody style={{ padding: 0 }}>
                                <div className='nav-modal-body'>
                                    <div className='mobileTopMenu'>
                                        <div className='mobile-nav-logo'>{logo}</div>
                                        {/* <button className='mobileSearchBtn' onClick={toggle}></button> */}
                                        {!user?.isAuthenticated ? signInLink : <a className='mobileProfileBtn' href={myProfileLink} />}
                                        {wishListIconMobile}
                                    </div>
                                    {_renderReactFragment(menuBar)}
                                    {_renderReactFragment(siteOptions)}
                                    {user?.isAuthenticated && (
                                        <a className='ms-header__signout-button msc-btn' href={user?.signOutUrl}>
                                            <span className='ms-header__signout-button-text'>{logOffButtonText}</span>
                                        </a>
                                    )}
                                </div>
                            </ModalBody>
                        </Modal>

                        <div className='row'>
                            <div className='web_nav'>{_renderReactFragment(menuBar)}</div>
                        </div>
                    </Node>
                </Module>
            </div>
        </>
    );
};

function _renderPopOverOptions(
    popOver: boolean,
    popOverRef: React.RefObject<HTMLButtonElement>,
    props: IHeaderViewProps & IHeaderProps<IHeaderData>,
    profileLink: string | undefined
): React.ReactNode {
    const {
        context: {
            request: { user }
        }
    } = props;
    const signInLinkProps = props?.signInLink as JSX.Element;

    return (
        profileLink && (
            <>
                {profileLink && <RichTextComponent />}
                <a
                    className={!user?.isAuthenticated ? 'default-style' : 'loggedIn'}
                    href={user?.isAuthenticated ? profileLink : signInLinkProps.props.href}
                >
                    <Button
                        innerRef={popOverRef}
                        className='ms-header__signin-button msc-btn'
                        aria-describedby='userOptionsPopover'
                        color='link'
                        aria-expanded={popOver}
                    />
                </a>
            </>
        )
    );
}

function getSignInDropdownPopoverConentContainer(
    popOver: boolean,
    togglePopOver: React.Dispatch<React.SetStateAction<boolean>>,
    popOverRef: React.RefObject<HTMLButtonElement>
): INodeProps {
    return {
        tag: Popover,
        id: 'userOptionsPopover',
        className: 'ms-header__account-info-content',
        placement: 'bottom',
        isOpen: popOver,
        target: popOverRef,
        toggle: togglePopOver
    };
}

function _renderPopOver(props: IHeaderViewProps & IHeaderProps<IHeaderData>, myProfileLink: string | undefined): JSX.Element {
    const {
        context: {
            request: { user }
        },
        resources: { myProfileButtonText, loginButtonText, signUpButtonText, logOffButtonText, newToOnly }
    } = props;

    return (
        <>
            {!user?.isAuthenticated && (
                <div>
                    <>
                        {user?.signInUrl && (
                            <a className='sign-in button' href={user?.signInUrl}>
                                {loginButtonText}
                            </a>
                        )}

                        <div className='parent-singup'>
                            {<RichTextComponent className='new-to-only' text={newToOnly} />}
                            {
                                <a className='sign-Up button' href={user?.signUpUrl} target={''}>
                                    {<RichTextComponent text={signUpButtonText} />}
                                </a>
                            }
                        </div>
                    </>
                </div>
            )}

            {user?.isAuthenticated && (
                <div>
                    <a className='sign-Up button' href={myProfileLink ? myProfileLink : '#'}>
                        {myProfileButtonText && <RichTextComponent text={myProfileButtonText} />}
                    </a>
                    <a className='sign-in button' href={user?.signOutUrl}>
                        {logOffButtonText}
                    </a>
                </div>
            )}
        </>
    );
}

function _renderDesktopAccountBlock(
    props: IHeaderViewProps & IHeaderProps<IHeaderData>,
    popOver: boolean,
    togglePopOver: React.Dispatch<React.SetStateAction<boolean>>,
    popOverRef: React.RefObject<HTMLButtonElement>,
    myProfileLink: string | undefined
): JSX.Element | null {
    const {
        context: {
            telemetry: { error },
            request: { user }
        },
        AccountInfoDropdownParentContainer
    } = props;
    const accountInfoDropdownButton = _renderPopOverOptions(popOver, popOverRef, props, myProfileLink);

    const AccountInfoDropdownPopoverConentContainer = getSignInDropdownPopoverConentContainer(popOver, togglePopOver, popOverRef);

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer} onMouseEnter={togglePopOver} onMouseLeave={togglePopOver}>
                    {accountInfoDropdownButton}

                    <Node {...AccountInfoDropdownPopoverConentContainer}>{_renderPopOver(props, myProfileLink)}</Node>
                </Node>
            );
        } else if (user?.signInUrl) {
            return <Node {...AccountInfoDropdownParentContainer}>{user?.signInUrl}</Node>;
        }
    }
    error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element {
    return (
        <React.Fragment>
            {items && items.length
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </React.Fragment>
    );
}

export default headerView;
